import axios from "axios";

import { authApi } from "../../utils/axios";
import { config } from "../../utils/config";

export const getUsers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 10,
      page: params.page || 1,
      ...(params.skip && { skip: params.skip }),
      ...(params.sortBy && { sortBy: params.sortBy }),
    });

    const response = await authApi({
      method: "GET",
      url: `/users?${queryParams}`,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchUsers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 10,
      page: params.page || 1,
      ...(params.skip && { skip: params.skip }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.query && { query: params.query }),
      ...(params.teamSport && { teamSport: params.teamSport }),
      ...(params.isMinor !== undefined && { isMinor: params.isMinor }),
      ...(params.isAdult !== undefined && { isAdult: params.isAdult }),
      ...(params.skipIds?.length && { skipIds: params.skipIds.join(",") }),
    });

    const response = await authApi({
      method: "GET",
      url: `/users/search?${queryParams}`,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchTeams = async (params) => {
  const { query, sportType } = params;
  const queryParams = new URLSearchParams({
    query,
    ...(sportType && { sportType }),
  });

  const response = await authApi({
    method: "GET",
    url: `/teams/search?${queryParams}`,
  });
  return response.data;
};

export const updateTeam = async (teamId, teamData) => {
  const response = await authApi({
    method: "PUT",
    url: `/teams/${teamId}`,
    data: teamData,
  });
  return response.data;
};

export const deleteTeam = async (teamId) => {
  const response = await authApi({
    method: "DELETE",
    url: `/teams/${teamId}`,
  });
  return response.data;
};

export const inviteTeam = async (
  tournamentId,
  categoryId,
  teamData
) => {
  const response = await authApi({
    method: "POST",
    url: `/tournaments/${tournamentId}/categories/${categoryId}/teams/invite`,
    data: {
      teamId: teamData.teamId,
      status: teamData.status,
      invitedOn: teamData.invitedOn,
      registrationFeeStatus: teamData.registrationFeeStatus,
      inviteValidTill: teamData.inviteValidTill,
    },
  });
  return response.data;
};

export const getAllTeams = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      ...params,
    });

    const response = await authApi({
      method: "GET",
      url: `/teams?${queryParams}`,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getTeamById = async (teamId) => {
  const response = await authApi({
    method: "GET",
    url: `/teams/${teamId}/team-dashboard`,
  });
  return response.data;
};

export const getTeamStats = async (teamId) => {
  const response = await authApi({
    method: "GET",
    url: `/teams/${teamId}/team-stats`,
  });
  return response.data;
};

export const getTeamOverview = async (teamId, data) => {
  const response = await authApi({
    method: "POST",
    url: `/teams/${teamId}/overview`,
    data,
  });
  return response.data;
};

export const inviteOrganisationAdmin = async (organisationId, payload) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/organisations/${organisationId}/invite-user`,
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const inviteUser = async (
  tournamentId,
  categoryId,
  payload
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/invite-user`,
      data: {
        ...payload,
        categoryId,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Invite a member (referee or admin) at tournament level
export const inviteMember = async (tournamentId, payload) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/invite-member`,
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Get member invitation details (public, no auth)
export const getMemberInvitation = async (token) => {
  try {
    const response = await axios.get(
      `${config.API_BASE_URL}/${config.API_PATH}/tournaments/member-invitation/${token}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Accept member invitation (auth required)
export const acceptMemberInvitation = async (token) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/member-invitation/${token}/accept`,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const fetchOrganisationalMembers = async (
  organisationId,
  payload,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/organisations/${organisationId}/organisational-members`,
      data: payload,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

// Delete single member (referee or admin)
export const deleteMember = async (tournamentId, memberId) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${tournamentId}/members/${memberId}`,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Bulk delete members (referees or admins)
export const bulkDeleteMembers = async (tournamentId, memberIds) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/members/bulk-delete`,
      data: { memberIds },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Bulk delete teams
export const bulkDeleteTeams = async (tournamentId, categoryId, teamIds) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/teams/bulk-delete`,
      data: { teamIds },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
